import type { RefObject } from 'preact';
import { useEffect, useState } from 'preact/hooks';

/**
 * Make an element draggable. Note that changing `enabled` or passing different ref objects
 * will trigger reconfiguring the event handlers, but changing `.current` will not.
 * @returns Whether the element is currently being dragged
 */
export function useDraggable(options: {
  /** Whether to enable dragging */
  enabled: boolean;
  /** Element that should be draggable */
  containerElementRef: RefObject<HTMLElement>;
  /** The drag handle element */
  dragElementRef: RefObject<HTMLElement>;
}): { isDragging: boolean } {
  const { enabled, containerElementRef, dragElementRef } = options;

  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const moveTarget = containerElementRef?.current;
    const dragTarget = dragElementRef?.current;

    if (!enabled || !moveTarget || !dragTarget) {
      return;
    }

    let originalX = 0;
    let originalY = 0;
    let offset = { x: 0, y: 0 };

    const onPointerMove = (event: PointerEvent) => {
      moveTarget.style.transform = `translate(${offset.x + event.clientX - originalX}px, ${
        offset.y + event.clientY - originalY
      }px)`;
    };

    const onPointerUp = (event: PointerEvent) => {
      setIsDragging(false);
      offset = {
        x: offset.x + event.clientX - originalX,
        y: offset.y + event.clientY - originalY,
      };

      dragTarget.releasePointerCapture(event.pointerId);
      dragTarget.removeEventListener('pointerup', onPointerUp);
      dragTarget.removeEventListener('pointermove', onPointerMove);

      event.preventDefault();
    };

    const onPointerDown = (event: PointerEvent) => {
      setIsDragging(true);
      originalX = event.clientX;
      originalY = event.clientY;
      dragTarget.setPointerCapture(event.pointerId);
      dragTarget.addEventListener('pointerup', onPointerUp);
      dragTarget.addEventListener('pointermove', onPointerMove);
      event.preventDefault();
    };

    dragTarget.addEventListener('pointerdown', onPointerDown);

    return () => {
      dragTarget.removeEventListener('pointerdown', onPointerDown);
      dragTarget.removeEventListener('pointermove', onPointerMove);
      dragTarget.removeEventListener('pointerup', onPointerUp);
    };
  }, [enabled, containerElementRef, dragElementRef]);

  return {
    isDragging,
  };
}
